import { useSpring, easings } from '@react-spring/web';
import { useEffect } from 'react';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';

import { AnimatedDiv } from '@/utils/animated-div';
import { classNames } from '@/utils/classnames';
import type { TokenInfo } from '@/utils/token.types';

type TokenSwapAnimationProps = {
  fromToken: TokenInfo;
  toToken: TokenInfo;
  className?: string;
  isComplete?: boolean;
};

// Delays then separates both tokens into their overlapping pending positions.
const getInitialAnimationConfig = (isFromToken?: boolean) => ({
  x: isFromToken ? -6 : 6,
  y: isFromToken ? 3 : -3,
  opacity: 1,
  scale: 1,
  config: { duration: 500, easing: easings.easeOutBack },
  delay: 1500,
});

// Centers the buy token while fading and shrinking the sell token on completion.
const getFinalAnimationConfig = (isFromToken?: boolean) => ({
  x: 0,
  y: 0,
  opacity: isFromToken ? 0 : 1,
  scale: isFromToken ? 0.8 : 1,
  config: { duration: 400, easing: easings.easeInOutCubic },
  delay: 1,
});

export const TokenSwapAnimation = ({
  fromToken,
  toToken,
  className,
  isComplete,
}: TokenSwapAnimationProps) => {
  const [fromTokenSpring, fromTokenApi] = useSpring(() => ({
    x: 0,
    y: 0,
    opacity: 1,
    scale: 1,
  }));

  const [toTokenSpring, toTokenApi] = useSpring(() => ({
    x: 0,
    y: 0,
    opacity: 0,
    scale: 1,
  }));

  useEffect(() => {
    fromTokenApi.stop();
    toTokenApi.stop();

    if (isComplete) {
      fromTokenApi.start(getFinalAnimationConfig(true));
      toTokenApi.start(getFinalAnimationConfig(false));
      return;
    }

    fromTokenApi.start(getInitialAnimationConfig(true));
    toTokenApi.start(getInitialAnimationConfig(false));
  }, [fromTokenApi, isComplete, toTokenApi]);

  const isCrossChain = fromToken.chainId !== toToken.chainId;

  return (
    <div className={classNames('relative size-10', className)} aria-hidden="true">
      <AnimatedDiv
        style={fromTokenSpring}
        className="absolute inset-0 flex items-center justify-center"
      >
        <TokenAvatar
          address={fromToken.address}
          chainId={fromToken.chainId}
          size={26}
          showChainLogo={isCrossChain}
        />
      </AnimatedDiv>

      <AnimatedDiv
        style={toTokenSpring}
        className="absolute inset-0 z-10 flex items-center justify-center"
      >
        <div
          className={classNames(
            !isComplete ? 'rounded-full size-6.5 ring-2 ring-token-gray-200' : undefined,
          )}
        >
          <TokenAvatar
            address={toToken.address}
            chainId={toToken.chainId}
            size={26}
            showChainLogo={isCrossChain}
          />
        </div>
      </AnimatedDiv>
    </div>
  );
};
